<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <p class="mb-0 main-identifier fw-semibold">Payments</p>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">View Payment Vouchers</p>
    </div>

    <div class="d-flex justify-content-center">
      <div class="form-outer" style="max-width: 1400px;">
        <div class="view-item position-relative">

          <div class="form-inner">
            <div class="form-header d-flex justify-content-between align-items-center report">
              <div>
                <h1 class="mb-0">Generated Payment Vouchers</h1>
                <p class="mb-0 small">View and manage payment vouchers</p>
              </div>
            </div>

            <div class="form-body">
              <!-- Filters -->
              <div class="filters-section mb-4">
                <div class="row g-3">
                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">MDA:</label>
                    <SearchableSelect v-model="filters.mda" :options="mdaFilterOptions" placeholder="All" small
                      clearable />
                  </div>

                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">Date Initiated:</label>
                    <select class="form-select form-select-sm" v-model="filters.date">
                      <option value="">All Time</option>
                      <option value="this_month">This Month</option>
                      <option value="last_month">Last Month</option>
                      <option value="this_year">This Year</option>
                    </select>
                  </div>

                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">Status:</label>
                    <select class="form-select form-select-sm" v-model="filters.status">
                      <option value="">All</option>
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="returned">Returned</option>
                    </select>
                  </div>

                  <div class="col-md-3 d-flex align-items-end">
                    <div class="w-100">
                      <label class="form-label small fw-semibold invisible">Action</label>
                      <button class="btn btn-sm btn-outline-secondary w-100" @click="clearFilters">
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Table Controls -->
              <div v-if="!loading" class="table-controls">
                <div class="table-controls-top">
                  <div class="table-controls-left">
                    <div class="entries-control">
                      <span>Show</span>
                      <select class="form-select form-select-sm" v-model.number="pageLength">
                        <option :value="10">10</option>
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                      </select>
                      <span>Entries</span>
                    </div>

                    <div class="search-box" style="max-width: 500px;">
                      <span class="material-symbols-outlined search-icon">search</span>
                      <input type="search" v-model="search" class="form-control form-control-sm search-input"
                        placeholder="Search by pv no, beneficiary, MDA, bank or net amount..." />
                    </div>
                  </div>

                  <div class="export-links">
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToPDF()">Export to
                      PDF</a>
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToExcel()">Export to
                      Excel</a>
                  </div>
                </div>

              </div>

              <!-- Table -->
              <div v-if="loading" class="loading-state text-center py-5">
                <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                <p class="mt-2 mb-0 text-muted">Loading payment vouchers...</p>
              </div>

              <div v-else class="table-wrapper">
                <div class="table-container">
                  <table class="responsive-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>PV No</th>
                        <th>Beneficiary</th>
                        <th>Originating MDA</th>
                        <th>Bank</th>
                        <th>Date Initiated</th>
                        <th>Net Amount</th>
                        <th>Payment Date </th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="paginatedItems.length === 0">
                        <td colspan="9" class="text-center text-muted">No payment vouchers to display.</td>
                      </tr>
                      <tr v-for="(pv, index) in paginatedItems" :key="pv.id"
                        :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                        <td data-label="S/N" class="serial-no text-center">{{ (currentPage - 1) * pageLength + index + 1
                          }}</td>
                        <td data-label="Payment Voucher No" class="primary-col fw-semibold" title="View Payment Voucher"
                          @click.prevent="viewPaymentVoucher(pv.id)">
                          <router-link :to="{ name: 'ViewPaymentVoucher', params: { id: pv.id } }"
                            class="view-link fw-semibold text-decoration-underline" @mouseenter="showTooltip($event, pv)"
                          @mouseleave="hideTooltip">
                            {{ pv.pv_no || 'N/A' }}
                          </router-link>
                        </td>
                        <td data-label="Beneficiary" class="secondary-col">{{ pv.beneficiary }}</td>
                        <td data-label="MDA" class="secondary-col">{{ pv.mda }}</td>
                        <td data-label="Bank" class="secondary-col">{{ pv.bank }}</td>
                        <td data-label="Date Initiated" class="secondary-col">{{ formatDate(pv.issue_date) }}</td>
                        <td data-label="Net Amount" class="secondary-col fw-semibold text-end amount">{{
                          formatCurrency(pv.net_amount) }}</td>
                        <td data-label="Payment Date" class="secondary-col">{{ formatDate(pv.payment_date) }}</td>
                        <td data-label="Status" class="secondary-col">{{ pv.status }}</td>
                        <!-- <td data-label="Action" class="action-col">
                          <a href="#" class="view-link text-decoration-none"
                            @click.prevent="viewPaymentVoucher(pv.id)">View</a>
                        </td> -->
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Tooltip -->
                <div v-if="tooltipVisible && hoveredNotification" class="custom-tooltip" :style="{
                  top: tooltipY + 'px',
                  left: tooltipX + 'px'
                }">
                  <div v-if="isLoadingDetails">
                    <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Loading details...
                  </div>
                  <p><strong>Details</strong></p>
                  <!-- PAYMENT VOUCHER -->
                    <p><strong>Warrant Number: </strong>{{ hoveredNotification.warrantNumber || 'N/A' }}</p>
                    <p><strong>Gross Amount: </strong>{{ formatCurrency(hoveredNotification.grossAmount || 0) }}</p>
                    <p><strong>Payment Type: </strong>{{ hoveredNotification.paymentType || 'N/A' }}</p>
                    <p><strong>Particulars: </strong>{{ hoveredNotification.particulars || 'N/A' }}</p>
                    <p><strong>Nature of Expenditure: </strong>{{ hoveredNotification.natureOfExpenditure || 'N/A' }}
                    </p>
                    <p><strong>Org Code: </strong> {{ hoveredNotification.orgCode || 'N/A' }}</p>
                </div>
              </div>

              <!-- Pagination Summary and Actions -->
              <div v-if="!loading" class="pagination-info">
                <small>
                  Showing {{ (currentPage - 1) * pageLength + 1 }} to
                  {{ Math.min(currentPage * pageLength, filteredItems.length) }} of
                  {{ filteredItems.length }} entries
                </small>

                <!-- Pagination Controls -->
                <nav class="pagination-nav">
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <button class="page-link" @click.prevent="changePage(currentPage - 1)">Previous</button>
                    </li>
                    <li v-for="page in displayedPages" :key="page" class="page-item"
                      :class="{ active: currentPage === page }">
                      <button class="page-link" @click.prevent="changePage(page)">{{ page }}</button>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <button class="page-link" @click.prevent="changePage(currentPage + 1)">Next</button>
                    </li>
                  </ul>
                </nav>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import { useRoute, useRouter } from 'vue-router'
import DashboardLayout from '../layout/DashboardLayout.vue'
import SearchableSelect from '../common/SearchableSelect.vue'
import { debounce } from 'lodash'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import ExcelJS from 'exceljs'
import '@/assets/css/table.css'
import '@/assets/css/responsive-table.css'

const pvs = ref([])
const mdas = ref([])
const loading = ref(true)
const isLoadingDetails = ref(true)
const search = ref('')
const pageLength = ref(10)
const currentPage = ref(1)
const root_api_url = process.env.VUE_APP_API_ROOT_URL
const router = useRouter()
const route = useRoute()

const hoveredNotification = ref(null)
const tooltipVisible = ref(false)
const tooltipX = ref(0)
const tooltipY = ref(0)

const showTooltip = async (event, pv) => {
  hoveredNotification.value = pv
  tooltipVisible.value = true

  const rect = event.currentTarget.getBoundingClientRect()

  tooltipX.value = rect.left + window.scrollX
  tooltipY.value = rect.bottom + window.scrollY + 8 // small offset

  await fetchUnlinkedPV(pv.id)
}

const hideTooltip = () => {
  tooltipVisible.value = false
}

const fetchUnlinkedPV = async (id) => {
  const token = Cookies.get('auth_token')
  try {
    const { data } = await axios.get(`${root_api_url}/payment/voucher/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    hoveredNotification.value = {
      ...hoveredNotification.value,
      ...data
    }
  } catch (err) {
    console.error('Error fetching Unlinked Payment Voucher:', err)
  } finally {
    isLoadingDetails.value = false
  }
}

const filters = ref({
  mda: '',
  date: '',
  status: ''
})

const debouncedSearch = ref('')
watch(search, debounce((val) => {
  debouncedSearch.value = val
  currentPage.value = 1
}, 300))

watch(pageLength, () => {
  currentPage.value = 1
})

watch(filters, () => {
  currentPage.value = 1
}, { deep: true })

const fetchPVs = async () => {
  const token = Cookies.get('auth_token')
  loading.value = true
  try {
    const response = await axios.get(`${root_api_url}/payment/vouchers/all`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    pvs.value = response.data.map(pv => ({
      id: pv.id,
      pv_no: pv.pvNumber ?? '',
      beneficiary: pv.payee ?? '',
      issue_date: pv.pvInitDate ?? '',
      mda: pv.mdaName ?? '',
      payment_date: pv.paymentDate ?? '',
      bank: pv.bankName ?? '',
      net_amount: Number(pv.netAmount ?? 0),
      status: pv.pvStatus ?? (
        pv.completedPVInd === 1 || pv.completed ? 'completed'
          : pv.returnedPVInd === 1 || pv.returned ? 'returned'
            : pv.pendingPVInd === 1 || pv.pending ? 'pending'
              : 'active'
      ),
      nature_of_expenditure: pv.natureOfExpenditure ?? 'N/A'

    }))
  } catch (error) {
    console.error('Failed to fetch pvs:', error)
    pvs.value = []
  } finally {
    loading.value = false
  }
}

const fetchMDAs = async () => {
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/eli/mda`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    mdas.value = response.data.map(mda => ({
      id: mda.id,
      name: mda.name,
      organisation_code: mda.orgCode,
    }))
  } catch (error) {
    console.error('Failed to fetch MDAs:', error)
  }
}

const uniqueMDAs = computed(() => {
  return [...mdas.value].sort((a, b) => a.name.localeCompare(b.name))
})

const mdaFilterOptions = computed(() =>
  uniqueMDAs.value.map(mda => ({ id: mda.name, name: mda.name }))
)

const filteredItems = computed(() => {
  let filtered = pvs.value

  // Apply search filter
  if (debouncedSearch.value) {
    const term = debouncedSearch.value.toLowerCase()
    filtered = filtered.filter(pv => {
      return (
        (pv.pv_no || '').toLowerCase().includes(term) ||
        (pv.beneficiary || '').toLowerCase().includes(term) ||
        (pv.mda || '').toLowerCase().includes(term) ||
        (pv.bank || '').toLowerCase().includes(term) ||
        String(pv.net_amount || '').includes(term)
      )
    })
  }

  // Apply MDA filter
  if (filters.value.mda) {
    filtered = filtered.filter(w => w.mda === filters.value.mda)
  }

  // Apply date filter
  if (filters.value.date) {
    const now = new Date()
    filtered = filtered.filter(w => {
      const issueDate = new Date(w.issue_date)
      switch (filters.value.date) {
        case 'this_month':
          return issueDate.getMonth() === now.getMonth() &&
            issueDate.getFullYear() === now.getFullYear()
        case 'last_month': {
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1)
          return issueDate.getMonth() === lastMonth.getMonth() &&
            issueDate.getFullYear() === lastMonth.getFullYear()
        }
        case 'this_year':
          return issueDate.getFullYear() === now.getFullYear()
        default:
          return true
      }
    })
  }

  // Apply date sorting (MOST RECENT FIRST)
  filtered = [...filtered].sort((a, b) => {
    return new Date(b.issue_date) - new Date(a.issue_date)
  })

  // Apply status filter
  if (filters.value.status) {
    filtered = filtered.filter(w => w.status.toLowerCase() === filters.value.status.toLowerCase())
  }

  return filtered
})

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * pageLength.value
  return filteredItems.value.slice(start, start + pageLength.value)
})

const totalPages = computed(() => {
  return Math.ceil(filteredItems.value.length / pageLength.value)
})

const displayedPages = computed(() => {
  const pages = []
  const maxDisplayed = 5
  let startPage = Math.max(1, currentPage.value - Math.floor(maxDisplayed / 2))
  let endPage = Math.min(totalPages.value, startPage + maxDisplayed - 1)

  if (endPage - startPage < maxDisplayed - 1) {
    startPage = Math.max(1, endPage - maxDisplayed + 1)
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i)
  }

  return pages
})

const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const clearFilters = () => {
  filters.value = {
    mda: '',
    date: '',
    status: ''
  }
  search.value = ''
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const formatCurrency = (amount) => {
  return '₦' + Number(amount || 0).toLocaleString('en-NG')
}

const viewPaymentVoucher = (id) => {
  router.push({ name: 'ViewPaymentVoucher', params: { id } })
}

const exportToPDF = () => {
  const doc = new jsPDF()

  // Add title
  doc.setFontSize(16)
  doc.text('Generated PVs', 14, 15)

  // Add date
  doc.setFontSize(10)
  doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 22)

  autoTable(doc, {
    startY: 30,
    head: [['PV No', 'Beneficiary', 'Originating MDA', 'Bank', 'Issue Date', 'Net Amount', 'Status']],
    body: filteredItems.value.map((pv) => [
      pv.pv_no,
      pv.beneficiary,
      pv.mda,
      pv.bank,
      formatDate(pv.issue_date),
      formatCurrency(pv.net_amount),
      pv.status
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [66, 139, 202] }
  })

  doc.save('pvs.pdf')
}

const exportToExcel = async () => {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('PVs')

  // Add headers
  worksheet.addRow(['PV No', 'Beneficiary', 'Originating MDA', 'Bank', 'Issue Date', 'Net Amount', 'Status'])

  // Add data
  filteredItems.value.forEach((pv) => {
    worksheet.addRow([
      pv.pv_no,
      pv.beneficiary,
      pv.mda,
      pv.bank,
      formatDate(pv.issue_date),
      formatCurrency(pv.net_amount),
      pv.status
    ])
  })

  // Style header row
  worksheet.getRow(1).font = { bold: true }
  worksheet.getRow(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' }
  }

  // Auto-fit columns
  worksheet.columns.forEach(column => {
    column.width = 20
  })

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'pvs.xlsx'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

onMounted(async () => {
  await fetchMDAs()
  await fetchPVs()
  if (route.query.status) {
    filters.value.status = route.query.status
  }
  document.title = "View Payment Vouchers"
})

</script>

<style scoped>
/* Fix horizontal scroll - override any parent overflow restrictions */
.view-item,
.form-inner,
.form-body {
  overflow: visible !important;
}

/* Component-specific styles */
.primary-col {
  font-weight: 500;
  color: #212529;
}

.row-even {
  background-color: #ffffff;
}

.row-odd {
  background-color:var(--table-bg-head);
}

.material-symbols-outlined {
  font-size: 1.25rem;
}

.view-link {
  color: #428bca;
  font-weight: 500;
}

.view-link:hover {
  color: #2a6496;
  text-decoration: underline !important;
}

.filters-section {
  background-color:var(--table-bg-head);
  padding: 1.5rem;
  border-radius: 0.375rem;
  border: 1px solid #dee2e6;
}

.form-label {
  margin-bottom: 0.25rem;
    color: var(--color);
}

.showing-info {
  padding-left: 0.5rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .filters-section .row {
    gap: 0.75rem;
  }

  .table-controls-top {
    flex-direction: column;
    gap: 1rem;
  }

  .export-links {
    width: 100%;
    display: flex;
    gap: 1rem;
  }

  .export-link {
    flex: 1;
    text-align: center;
  }
}

.custom-tooltip {
  position: fixed;
  z-index: 9999;
  background: #ffffff;
  color: #000;
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 13px;
  max-width: 350px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  pointer-events: none;
  animation: fadeIn 0.15s ease-in-out;
}

.custom-tooltip p {
  margin: 0 0 6px;
}

.custom-tooltip p:last-child {
  margin-bottom: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
